import { getHeaderDomain, rem } from '@dustin-web/microsite-config'
import React, { createContext, Dispatch, Reducer, useEffect, useReducer, useState } from 'react'
import { useSiteContext } from '../use-site-context'
import { ScrollOffset } from '../use-scroll-offset'
import { usePathname } from 'next/navigation'
import { useResponsive } from './use-responsive'

enum SubMenu {
  PRODUCTS = 'PRODUCTS',
  SOLUTIONS = 'SOLUTIONS',
}

const HEADER_MOBILE_HEIGHT = 10
const HEADER_DESKTOP_HEIGHT = 5.2 + 4.8
export const USP_BANNER_HEIGHT = 3.3

export type LayoutHeader = {
  currentDomain: string
  isProductOpen: boolean
  height: {
    rem: number
    px: number // TODO: check if this should be deprecated
    css: string
  }
}

export const getDefaultContext = () => ({
  currentDomain: '',
  isProductOpen: false,
  height: {
    rem: HEADER_MOBILE_HEIGHT,
    px: rem(HEADER_MOBILE_HEIGHT),
    css: `${HEADER_MOBILE_HEIGHT}rem`,
  },
})

export enum LayoutHeaderActionType {
  SET_ACTIVE_SUB_MENU,
  RESET_STATE,
}

export interface LayoutHeaderAction {
  type: LayoutHeaderActionType
  payload?: {
    currentDomain?: string
    subMenu?: SubMenu | null
  }
}

type HeaderState = {
  currentDomain: string
  isProductOpen: boolean
}

const headerReducer: Reducer<HeaderState, LayoutHeaderAction> = (prevState, action) => {
  const isProductOpen = action.payload?.subMenu === SubMenu.PRODUCTS && !prevState.isProductOpen

  switch (action.type) {
    case LayoutHeaderActionType.SET_ACTIVE_SUB_MENU:
      return {
        ...prevState,
        isProductOpen,
      }
    case LayoutHeaderActionType.RESET_STATE:
      return { ...prevState, ...action.payload }
    default:
      return prevState
  }
}

export const LayoutHeaderContext = createContext<{
  header: LayoutHeader
  dispatchHeader: Dispatch<LayoutHeaderAction>
}>({
  header: getDefaultContext(),
  dispatchHeader: () => null,
})

/**
 * Context provider that holds header
 */
export const LayoutHeaderProvider = ({ children }: { children: React.ReactNode }) => {
  const { sourcingCompany } = useSiteContext()
  const url = usePathname() || ''
  const currentDomain = getHeaderDomain(url, sourcingCompany)

  const getDefaultHeaderState = () => {
    return {
      currentDomain: currentDomain,
      isProductOpen: false,
    }
  }

  const [headerState, dispatchHeader] = useReducer(headerReducer, getDefaultHeaderState())

  useEffect(() => {
    dispatchHeader({ type: LayoutHeaderActionType.RESET_STATE, payload: getDefaultHeaderState() })
  }, [url])

  const { isSmallScreen } = useResponsive()

  const getDefaultOffset = () => {
    if (!isSmallScreen) {
      return HEADER_DESKTOP_HEIGHT
    } else {
      return HEADER_MOBILE_HEIGHT
    }
  }

  const [offset, setOffset] = useState(getDefaultOffset())

  useEffect(() => {
    if (!isSmallScreen) {
      document.body.classList.remove('mobile-header')
    } else {
      if (!document.body.classList.contains('mobile-header'))
        document.body.classList.add('mobile-header')
    }
  }, [isSmallScreen])

  useEffect(() => {
    setOffset(getDefaultOffset())
  }, [isSmallScreen])

  const state = {
    header: {
      ...headerState,
      height: {
        rem: offset,
        px: rem(offset),
        css: `${offset}rem`,
      },
    },
    dispatchHeader,
  }

  const scrollOffset = {
    top: state.header.height.px,
  }

  return (
    <ScrollOffset.Provider value={scrollOffset}>
      <LayoutHeaderContext.Provider value={state}>{children}</LayoutHeaderContext.Provider>
    </ScrollOffset.Provider>
  )
}
